(ns hai.library.sources.show
  "Displays details for a specific source + allows editing some of the attributes."
  (:require
   [hai-model.model :as model]
   [hai.library.sources.documents-table :as documents-table]
   [hai.ui.components :as c]
   [hai.ui.components.form.core :as form]
   [hai.ui.components.form.input :as input]
   [hai.ui.components.spinners :as spinners]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e]]
   [hai.web-app.layout :as layout]
   [lambdaisland.ornament :as o]
   [malli.util :as mu]))

(o/defstyled edit-form :div
  :flex :flex-col :gap-2
  [:button :w-64px]
  ([source]
   [form/component
    {:id ::form
     :schema (mu/select-keys model/Source [:ai-source/uuid :ai-source/title])
     :initial-value source
     :on-submit (fn [updated-source]
                  (>e [:resolve [(list 'source/update updated-source)]
                       {:on-success-fx [[:dispatch [:form/submitted ::form]]
                                        [:dispatch [:toast/success {:text "Title updated"}]]
                                        [:dispatch [:entity/resolve updated-source]]]}]))
     :render (fn [{:form/keys [on-submit valid? submitting?]
                   {:ai-source/keys [title]} :form/fields}]
               [:<>
                [c/Text {:weight "bold" :mb "2"} "Title"]
                [input/field title]
                [c/Button
                 {:disabled (or (not valid?)
                                submitting?)
                  :on-click on-submit}
                 "Save"]])}]))

(o/defstyled error-card :div
  :mb-2
  ([{:keys [error-message failed-at job-name]}]
   [:<>
    [c/Card {:variant "surface"}
     [c/Box {:p "3"}
      [c/Flex {:direction "column" :gap "2"}
       [c/Text {:weight "bold" :color "red"} error-message]
       (when job-name
         [c/Text {:size "1" :color "gray"} (str "Job: " job-name)])
       (when failed-at
         [c/Text {:size "1" :color "gray"} (str "Failed: " failed-at)])]]]]))

(o/defstyled errors-section :div
  ([{:ai-source/keys [failures]}]
   [:<>
    (when (seq failures)
      [c/Box
       [c/Heading {:size "4" :mb "2"} "Errors"]
       [c/CalloutRoot {:variant "surface" :color "red"}
        [c/CalloutIcon [icons/outlined-info]]
        [c/CalloutText
         (str "This source has " (count failures) " error" (when (> (count failures) 1) "s") ".")]]
       [c/Box {:mt "3"}
        (for [failure failures]
          ^{:key (str (:failed-at failure) "-" (:job-name failure))}
          [error-card failure])]])]))

(defn configuration
  [source]
  [c/Box
   [c/Heading {:size "4" :mb "2"} "Configure Source"]
   [c/Card
    [c/Box {:width "300px"}
     [edit-form source]]]])

(defn details
  [source]
  [c/Box
   [c/Heading {:size "4" :mb "2"} "Source Details"]
   [c/Card
    [c/Flex {:direction "column" :gap "2" :p "4"}
     [c/Text {:size "2"} [:strong "Title: "] (:ai-source/title source)]
     [c/Text {:size "2"} [:strong "Type: "] (:ai-source/type-label source)]
     [c/Text {:size "2"} [:strong "UUID: "] (str (:ai-source/uuid source))]
     (when (:ai-source/status source)
       [c/Text {:size "2"} [:strong "Status: "] (name (:ai-source/status source))])]]])

(defn documents-section
  [source]
  (let [doc-count (count (:ai-source/documents source))]
    [c/Box
     [c/Heading {:size "4" :mb "2"}
      (str "Documents (" doc-count ")")]
     [c/Card
      [c/Box {:p "4"}
       [documents-table/documents-table source]]]]))

(defn page [req]
  (let [source-uuid (-> @req :parameters :path :ai-source/uuid)
        assistant-uuid (-> @req :parameters :query :ai-model-config/uuid)
        request-id [:ai-sources/show source-uuid]
        fetch-state @(<s [:fetch/state request-id])
        source @(<s [:entity {:ai-source/uuid source-uuid}])
        assistant (when assistant-uuid
                    @(<s [:entity {:ai-model-config/uuid assistant-uuid}]))]
    (cond
      ;; Loading state
      (= fetch-state :start)
      [c/Flex {:p "8" :justify "center" :align "center"}
       [spinners/pulse-dots]]

      ;; Error state
      (= fetch-state :error)
      [c/Flex {:p "8"}
       [:div "Failed to load source"]]

      ;; Source loaded successfully
      (some? source)
      [layout/page
       [layout/breadcrumbs
        {:breadcrumbs (if assistant
                        [:tasks/knowledge
                         {:route [:tasks/knowledge-config {:ai-model-config/uuid (:ai-model-config/uuid assistant)}]
                          :label (:ai-model-config/title assistant)}
                         (:ai-source/title source)]
                        [(:ai-source/title source)])}]
       [layout/heading {:label (:ai-source/title source)}]
       [layout/content
        [c/Box {:max-width "800px"}
         [c/Flex {:direction "column" :gap "4"}
          [configuration source]
          [errors-section source]
          [details source]
          [documents-section source]]]]]

      ;; Not found (fetch completed but entity is nil)
      :else
      [c/Flex {:p "8"}
       [:div "Source not found"]])))
